import React, { useState, useRef } from 'react';
import jsPDF from 'jspdf';
import SEO from './SEO';

interface ImageFile {
  file: File;
  preview: string;
  id: string;
}

const ImageToPdf: React.FC = () => {
  const [images, setImages] = useState<ImageFile[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const newImages: ImageFile[] = [];
    Array.from(files).forEach((file) => {
      if (file.type.startsWith('image/')) {
        const id = Math.random().toString(36).substr(2, 9);
        const preview = URL.createObjectURL(file);
        newImages.push({ file, preview, id });
      }
    });

    setImages(prev => [...prev, ...newImages]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const removeImage = (id: string) => {
    setImages(prev => {
      const updated = prev.filter(img => img.id !== id);
      // Clean up object URL
      const removed = prev.find(img => img.id === id);
      if (removed) {
        URL.revokeObjectURL(removed.preview);
      }
      return updated;
    });
  };

  const moveImage = (fromIndex: number, toIndex: number) => {
    const newImages = [...images];
    const [movedImage] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, movedImage);
    setImages(newImages);
  };

  const generatePDF = async () => {
    if (images.length === 0) return;

    setIsProcessing(true);
    try {
      const pdf = new jsPDF();
      let isFirstPage = true;

      for (const imageFile of images) {
        const img = new Image();
        img.src = imageFile.preview;
        
        await new Promise((resolve) => {
          img.onload = () => {
            if (!isFirstPage) {
              pdf.addPage();
            }
            isFirstPage = false;

            // Calculate dimensions to fit the page
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 10;
            const maxWidth = pageWidth - 2 * margin;
            const maxHeight = pageHeight - 2 * margin;

            let { width, height } = img;
            
            // Scale image to fit page while maintaining aspect ratio
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }

            // Center the image
            const x = (pageWidth - width) / 2;
            const y = (pageHeight - height) / 2;

            pdf.addImage(img, 'JPEG', x, y, width, height);
            resolve(void 0);
          };
        });
      }

      // Save the PDF
      pdf.save('images-to-pdf.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('生成PDF时出现错误，请重试');
    } finally {
      setIsProcessing(false);
    }
  };

  const clearAll = () => {
    images.forEach(img => URL.revokeObjectURL(img.preview));
    setImages([]);
  };

  return (
    <div className="tool-container">
      <SEO 
        title="图片转PDF工具 - 在线合并多张图片为PDF"
        description="免费在线图片转PDF工具，支持批量上传多张图片，一键合并生成PDF文件。支持JPG、PNG、GIF等格式，操作简单，无需安装软件。"
        keywords="图片转PDF,图片合并PDF,在线PDF制作,批量图片转换,免费PDF工具"
      />
      <h1 className="tool-title">图片转PDF</h1>
      <p className="tool-description">
        将多张图片合并成一个PDF文件，支持拖拽上传和排序
      </p>

      <div className="form-group">
        <div 
          className="file-upload"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => fileInputRef.current?.click()}
        >
          <div style={{ fontSize: '3rem', marginBottom: '15px' }}>📁</div>
          <p>点击选择图片或拖拽图片到此处</p>
          <p style={{ fontSize: '0.9rem', color: '#7f8c8d', marginTop: '10px' }}>
            支持 JPG, PNG, GIF, WebP 等格式
          </p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => handleFileSelect(e.target.files)}
        />
      </div>

      {images.length > 0 && (
        <div className="result-container">
          <div className="result-title">
            已选择 {images.length} 张图片
            <button 
              className="btn btn-secondary" 
              onClick={clearAll}
              style={{ marginLeft: '15px', padding: '6px 12px', fontSize: '14px' }}
            >
              清空所有
            </button>
          </div>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', 
            gap: '15px',
            marginTop: '15px'
          }}>
            {images.map((image, index) => (
              <div key={image.id} style={{
                position: 'relative',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                <img 
                  src={image.preview} 
                  alt={`Preview ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '120px',
                    objectFit: 'cover'
                  }}
                />
                <div style={{
                  position: 'absolute',
                  top: '5px',
                  right: '5px',
                  display: 'flex',
                  gap: '5px'
                }}>
                  {index > 0 && (
                    <button
                      onClick={() => moveImage(index, index - 1)}
                      style={{
                        background: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        padding: '2px 6px',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      ↑
                    </button>
                  )}
                  {index < images.length - 1 && (
                    <button
                      onClick={() => moveImage(index, index + 1)}
                      style={{
                        background: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        padding: '2px 6px',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      ↓
                    </button>
                  )}
                  <button
                    onClick={() => removeImage(image.id)}
                    style={{
                      background: 'rgba(220,53,69,0.8)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      padding: '2px 6px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    ×
                  </button>
                </div>
                <div style={{
                  position: 'absolute',
                  bottom: '0',
                  left: '0',
                  right: '0',
                  background: 'rgba(0,0,0,0.7)',
                  color: 'white',
                  padding: '5px',
                  fontSize: '12px',
                  textAlign: 'center'
                }}>
                  {index + 1}
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: '20px', textAlign: 'center' }}>
            <button 
              className="btn btn-primary"
              onClick={generatePDF}
              disabled={isProcessing}
              style={{ fontSize: '16px', padding: '12px 30px' }}
            >
              {isProcessing ? '生成中...' : '生成PDF'}
            </button>
          </div>
        </div>
      )}

      <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
        <h3 style={{ color: '#2c3e50', marginBottom: '15px' }}>使用说明</h3>
        <ul style={{ color: '#7f8c8d', lineHeight: '1.6' }}>
          <li>支持同时选择多张图片</li>
          <li>可以通过拖拽方式上传图片</li>
          <li>使用↑↓按钮调整图片在PDF中的顺序</li>
          <li>点击×按钮删除不需要的图片</li>
          <li>生成的PDF会自动适应页面大小</li>
        </ul>
      </div>
    </div>
  );
};

export default ImageToPdf;